/**
 * Who can create content inside a space.
 *
 * The field is `Space.canCreate` in the schema — the setting the wizard calls
 * "who can post". It has always existed and **no screen in the app looked at
 * it**: events, content and calls showed the create button to anyone logged in,
 * and those who could not found out from the 403.
 */
export type CanCreate = 'anyone' | 'staff' | 'admins'

/** SuperAdmin > owner > admin > moderator > member. Finance is a side role. */
const ADMINS = ['owner', 'admin']

export function canCreateInSpace(
  canCreate: CanCreate | undefined,
  role: string | undefined,
  isSuperAdmin = false,
  isAuthenticated = false,
): boolean {
  if (isSuperAdmin) return true
  if (!isAuthenticated) return false
  switch (canCreate) {
    case 'admins': return ADMINS.includes(role ?? '')
    case 'staff': return STAFF.includes(role ?? '')
    /* `anyone` e ausente caem aqui: o default do schema é `anyone`, e space
       antigo sem o campo preenchido precisa continuar funcionando. */
    default: return true
  }
}

/**
 * Finance (F-32) is a side role, not a rung on the ladder: it reads the
 * community's money — balance, ledger, payouts, products, fees, payout account —
 * and changes none of it. Outside Monetization it is a plain member, so it is
 * deliberately absent from `STAFF` and `ADMINS`.
 */
export const FINANCE_ROLE = 'finance'

/**
 * AUTH-7: "the team" (equipe) is admin + moderator + finance, owner always.
 * It decides visibility and the day-to-day community tools (moderation,
 * "Staff only" creation, members, groups, tags, org chart, reports). Money
 * settings, community settings, course/space editing, sending campaigns and
 * assigning roles stay owner/admin.
 */
export const TEAM_ROLES = ['owner', 'admin', 'moderator', FINANCE_ROLE]
const STAFF = TEAM_ROLES

/** Who opens the Monetization page. The API answers the same reads to these. */
export const MONETIZATION_ROLES = ['owner', 'admin', FINANCE_ROLE]

/** Who creates, edits and deletes there, and requests payouts. */
export const MONETIZATION_MANAGERS = ['owner', 'admin']

/** Who edits products (SEC-83): the API's PRODUCT_EDIT_ROLES. Creating and deleting stay with the managers. */
export const PRODUCT_EDITORS = ['owner', 'admin', FINANCE_ROLE]

/** Who may hand out the finance role (`PUT /members/:id/role` refuses anyone else). */
export const FINANCE_ASSIGNERS = ['owner', 'admin']

/**
 * Who acts on the community's subscriptions (F-45): cancel, reactivate,
 * discount, add-ons, extend, payment link and manual creation. Finance acts
 * here too, by David's decision; the API audits every action and refuses
 * anyone else with 403.
 */
export const SUBSCRIPTION_MANAGERS = ['owner', 'admin', FINANCE_ROLE]

/**
 * F-73 e-mail campaigns: a community-level area like Monetization, not a space
 * module. The whole team sees it, writes drafts and sends tests; only owner and
 * admin send (they can hurt the community's sender reputation). The API answers
 * the same roles.
 */
export const CAMPAIGN_ROLES = TEAM_ROLES

/** Who may send a campaign. */
export const CAMPAIGN_SENDERS = ['owner', 'admin']

/** Administração › Logs: owner and admin read every type; finance reads only `financial` (the API answers 403 otherwise). */
export const LOGS_ROLES = ['owner', 'admin', FINANCE_ROLE]
export const LOGS_ALL_TYPES_ROLES = ['owner', 'admin']

/** Administração › Engajamento (F-87): the whole team, like `GET /api/reports/engagement` and its statement. */
export const ENGAGEMENT_ROLES = TEAM_ROLES
/** F-148/F-153: the badge catalog, giving and taking badges, and "Ajustar pontos" (the API refuses anyone else). */
export const BADGE_MANAGERS = ['owner', 'admin']
/** F-141: who opens a workflow in the editor from elsewhere (a badge's origin, for one). */
export const WORKFLOW_MANAGERS = ['owner', 'admin']

/** Administração › Analytics (F-88): owner and admin read everything; finance reads only the purchase funnel. The API answers the same. */
export const ANALYTICS_ROLES = ['owner', 'admin', FINANCE_ROLE]
export const ANALYTICS_FULL_ROLES = ['owner', 'admin']

/**
 * Administração › Empresas (F-93 1A): owner and admin manage; finance reads everything; the moderator
 * reads the list and the team without the contract (the API leaves those fields out). A member gets 403.
 */
export const COMPANY_VIEWERS = TEAM_ROLES
export const COMPANY_MANAGERS = ['owner', 'admin']
